import { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router';
import { queryClient } from '#client/utils/queryClient.js';
import axios from 'axios';
import { TextField, IndexTable, IndexFilters, Checkbox, Text, ChoiceList, useBreakpoints } from '@shopify/polaris';
import './AppDataTable.css';
import { SessionContext } from '#client/contexts/SessionContext.jsx';

const UTCOffset = new Date().getTimezoneOffset() / 60;

function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
}

function mergeFilters(filters, newFilter) {
    const existingValue = getFilterValue(filters, newFilter.key);

    // Remove all existing filters with the same key
    const filteredArray = filters.filter((filter) => filter.key !== newFilter.key);

    newFilter.value = typeof existingValue === 'object' && !Array.isArray(existingValue) ? { ...existingValue, ...newFilter.value } : newFilter.value;

    newFilter.value = Array.isArray(existingValue) ? [...existingValue, ...newFilter.value] : newFilter.value;

    if (newFilter.value.from && newFilter.value.to) {
        newFilter.label = `${newFilter.friendlyFieldName} between ${newFilter.value.from} and ${newFilter.value.to}`;
    } else if (newFilter.value.from) {
        newFilter.label = `${newFilter.friendlyFieldName} after ${newFilter.value.from}`;
    } else if (newFilter.value.to) {
        newFilter.label = `${newFilter.friendlyFieldName} before ${newFilter.value.to}`;
    }
    // Add the new filter
    return [...filteredArray, newFilter];
}
function getFilterValue(filters, key) {
    return filters.find((filter) => filter.key === key)?.value ?? '';
}

// Convert filters to object for query string
function filtersToObject(appliedFilters) {
    return appliedFilters.reduce((acc, filter) => {
        if (filter.value.from) {
            acc[filter.key + '_from'] = filter.value.from;
        }
        if (filter.value.to) {
            acc[filter.key + '_to'] = filter.value.to;
        }
        if (typeof filter.value === 'string') {
            acc[filter.key] = filter.value;
        }
        return acc;
    }, {});
}

export default function AppDataTable({ DataTableConfig }) {
    // State
    const { currentUser } = useContext(SessionContext);

    // Create a unique localStorage key for this table
    const localStorageKey = `appDataTable_${DataTableConfig.objectName}`;

    // Helper function to load saved state from localStorage
    const loadSavedState = () => {
        try {
            const saved = localStorage.getItem(localStorageKey);
            if (saved) {
                return JSON.parse(saved);
            }
        } catch (error) {
            console.error('Error loading saved table state:', error);
        }
        return null;
    };

    const savedState = loadSavedState();

    const [page, setPage] = useState(savedState?.page || 1);

    const [searchTerm, setSearchTerm] = useState(savedState?.searchTerm || '');
    const debouncedSearchTerm = useDebounce(searchTerm, 500);

    const defaultSortCol = DataTableConfig.columns.find((c) => c.defaultSortDirection);
    const defaultSortDirection = [defaultSortCol?.key, defaultSortCol?.defaultSortDirection].join(' ') || ['id desc'];
    if (savedState?.sortSelected && !Array.isArray(savedState?.sortSelected)) {
        savedState.sortSelected = [savedState.sortSelected];
    }
    const [sortSelected, setSortSelected] = useState(savedState?.sortSelected || [defaultSortDirection]);
    const [filters, setFilters] = useState(savedState?.filters.map((filter) => ({ ...filter, onRemove: () => setFilters(filters.filter((f) => filter.key !== f.key)) })) || []);
    const debouncedFilters = useDebounce(filters, 500);

    const navigate = useNavigate();

    const [dataState, setDataState] = useState({
        rows: [],
        total: 0,
        page: 1,
        pageCount: 1,
        pageSize: 12,
        isLoading: true,
    });

    // State to store dynamically loaded filter options
    const [loadedFilterOptions, setLoadedFilterOptions] = useState({});

    // Load async filter options on mount
    useEffect(() => {
        const loadAsyncFilterOptions = async () => {
            const optionsToLoad = {};

            for (const column of DataTableConfig.columns) {
                if (column.isFilterable && column.filterOptions && typeof column.filterOptions === 'function') {
                    try {
                        const result = await column.filterOptions();
                        optionsToLoad[column.key] = result.data || result;
                    } catch (error) {
                        console.error(`Error loading filter options for ${column.key}:`, error);
                        optionsToLoad[column.key] = [];
                    }
                }
            }

            if (Object.keys(optionsToLoad).length > 0) {
                setLoadedFilterOptions(optionsToLoad);
            }
        };

        loadAsyncFilterOptions();
    }, [DataTableConfig.columns]);

    // Save state to localStorage whenever filters, page, searchTerm, or sortSelected changes
    useEffect(() => {
        try {
            const stateToSave = {
                filters,
                page,
                searchTerm,
                sortSelected,
            };
            localStorage.setItem(localStorageKey, JSON.stringify(stateToSave));
        } catch (error) {
            console.error('Error saving table state to localStorage:', error);
        }
    }, [filters, page, searchTerm, sortSelected, localStorageKey]);

    // Reset page when filters change
    useEffect(() => {
        setPage(1);
    }, [filters, searchTerm]);

    // Handlers
    function handleFiltersClearAll() {
        setFilters([]);
        setSearchTerm('');
        setSortSelected(defaultSortDirection);
        setPage(1);
    }

    const filterOptions = DataTableConfig.columns
        .filter((col) => col.isFilterable)
        .map(function (column) {
            if (column.filterOptions) {
                // Get filter options - either from static array or loaded async options
                const options = Array.isArray(column.filterOptions) ? column.filterOptions : loadedFilterOptions[column.key] || [];

                // Handle different filter types
                if (column.filterType === 'checkbox' || column.filterType === 'multiselect') {
                    return {
                        key: column.key,
                        label: column.title,
                        filter: (
                            <>
                                {options.map((option) => {
                                    const currentFilterValue = getFilterValue(filters, column.key);
                                    const currentValues = currentFilterValue ? currentFilterValue.split(';') : [];
                                    const isChecked = currentValues.includes(option.value);

                                    return (
                                        <Checkbox
                                            key={option.value}
                                            label={option.title}
                                            checked={isChecked}
                                            onChange={(checked) => {
                                                let newValues;
                                                if (checked) {
                                                    newValues = [...currentValues, option.value];
                                                } else {
                                                    newValues = currentValues.filter((v) => v !== option.value);
                                                }

                                                const newFilterValue = newValues.join(';');

                                                if (newFilterValue) {
                                                    // If there are values, update the filter
                                                    const selectedLabels = options
                                                        .filter((opt) => newValues.includes(opt.value))
                                                        .map((opt) => opt.title)
                                                        .join(', ');

                                                    setFilters(
                                                        mergeFilters(filters, {
                                                            key: column.key,
                                                            value: newFilterValue,
                                                            label: `${column.title}: ${selectedLabels}`,
                                                            onRemove: () => setFilters(filters.filter((f) => f.key !== column.key)),
                                                        })
                                                    );
                                                } else {
                                                    setFilters(filters.filter((f) => f.key !== column.key));
                                                }
                                            }}
                                        />
                                    );
                                })}
                            </>
                        ),
                    };
                } else if (column.filterType === 'picklist') {
                    return {
                        key: column.key,
                        label: column.title,
                        filter: (
                            <ChoiceList
                                title={column.title}
                                titleHidden
                                choices={options.map((opt) => ({ label: opt.title, value: opt.value }))}
                                selected={[getFilterValue(filters, column.key)].filter(Boolean)}
                                onChange={(selected) => {
                                    if (selected.length > 0) {
                                        const selectedOption = options.find((opt) => opt.value === selected[0]);
                                        setFilters(
                                            mergeFilters(filters, {
                                                key: column.key,
                                                value: selected[0],
                                                label: `${column.title}: ${selectedOption?.title || selected[0]}`,
                                                onRemove: () => setFilters(filters.filter((f) => f.key !== column.key)),
                                            })
                                        );
                                    } else {
                                        setFilters(filters.filter((f) => f.key !== column.key));
                                    }
                                }}
                            />
                        ),
                    };
                } else {
                    // Default to checkbox behavior if filterType not specified
                    return {
                        key: column.key,
                        label: column.title,
                        filter: (
                            <>
                                {options.map((option) => {
                                    const currentFilterValue = getFilterValue(filters, column.key);
                                    const currentValues = currentFilterValue ? currentFilterValue.split(';') : [];
                                    const isChecked = currentValues.includes(option.value);

                                    return (
                                        <Checkbox
                                            key={option.value}
                                            label={option.title}
                                            checked={isChecked}
                                            onChange={(checked) => {
                                                let newValues;
                                                if (checked) {
                                                    newValues = [...currentValues, option.value];
                                                } else {
                                                    newValues = currentValues.filter((v) => v !== option.value);
                                                }

                                                const newFilterValue = newValues.join(';');

                                                if (newFilterValue) {
                                                    const selectedLabels = options
                                                        .filter((opt) => newValues.includes(opt.value))
                                                        .map((opt) => opt.title)
                                                        .join(', ');

                                                    setFilters(
                                                        mergeFilters(filters, {
                                                            key: column.key,
                                                            value: newFilterValue,
                                                            label: `${column.title}: ${selectedLabels}`,
                                                            onRemove: () => setFilters(filters.filter((f) => f.key !== column.key)),
                                                        })
                                                    );
                                                } else {
                                                    setFilters(filters.filter((f) => f.key !== column.key));
                                                }
                                            }}
                                        />
                                    );
                                })}
                            </>
                        ),
                    };
                }
            } else if (column.type === 'text' || column.type === 'number') {
                return {
                    key: column.key,
                    label: column.title,
                    filter: (
                        <TextField
                            label={column.title}
                            value={getFilterValue(filters, column.key)}
                            autoComplete="off"
                            labelHidden
                            onChange={(value) =>
                                setFilters(
                                    mergeFilters(filters, {
                                        key: column.key,
                                        value: value,
                                        label: `${column.title}: ${value}`,
                                        onRemove: () => setFilters(filters.filter((f) => f.key !== column.key)),
                                    })
                                )
                            }
                        />
                    ),
                };
            } else if (column.type === 'date') {
                return {
                    key: column.key,
                    label: column.title,
                    filter: (
                        <>
                            From:{' '}
                            <TextField
                                label={column.title}
                                value={getFilterValue(filters, `${column.key}`).from}
                                onChange={(value) =>
                                    setFilters(
                                        mergeFilters(filters, {
                                            key: `${column.key}`,
                                            value: { from: value },
                                            label: `${column.title} after: ${value}`,
                                            friendlyFieldName: column.title,
                                            onRemove: () => setFilters(filters.filter((f) => f.key !== column.key)),
                                        })
                                    )
                                }
                                autoComplete="off"
                                labelHidden
                                type="date"
                            />
                            To:
                            <TextField
                                label={column.title}
                                value={getFilterValue(filters, `${column.key}`).to}
                                onChange={(value) =>
                                    setFilters(
                                        mergeFilters(filters, {
                                            key: `${column.key}`,
                                            value: { to: value },
                                            label: `${column.title} before: ${value}`,
                                            friendlyFieldName: column.title,
                                            onRemove: () => setFilters(filters.filter((f) => f.key !== column.key)),
                                        })
                                    )
                                }
                                autoComplete="off"
                                labelHidden
                                type="date"
                            />
                        </>
                    ),
                };
            }
        });

    async function fetchPaginatedRecords() {
        const response = await queryClient.fetchQuery({
            queryKey: [DataTableConfig.objectName, { searchTerm, sortSelected, filters, page }],
            queryFn: async () => {
                const params = new URLSearchParams({ ...filtersToObject(filters), page, sort: sortSelected, UTCOffset, search: searchTerm });
                const response = await axios.get(`${import.meta.env.VITE_API_URL}${DataTableConfig.resourceURL}`, { params });

                return {
                    rows: response.data.rows ?? [],
                    total: typeof response.data.total === 'number' ? response.data.total : null,
                    page: response.data.page,
                    pageCount: response.data.pageCount,
                    pageSize: response.data.pageSize,
                };
            },
        });
        return response;
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await fetchPaginatedRecords();
                setDataState({ ...data, isLoading: false });
            } catch (error) {
                console.error('❌ fetchPaginatedRecords error:', error);
            }
        };
        fetchData();
    }, [sortSelected, page, debouncedFilters, debouncedSearchTerm, DataTableConfig.reloadOnLocationChange ? currentUser?.activeStore : null]);

    const rowMarkup = dataState?.rows.map((row, index) => (
        <IndexTable.Row id={row.id} key={row.id} position={index}>
            {DataTableConfig.columns
                .filter((column) => column.isViewable)
                .map((column) => {
                    const accessor = column.accessor ?? ((value) => value);
                    return (
                        <IndexTable.Cell key={column.key}>
                            <Text as="span">{accessor(row[column.key], row, navigate)}</Text>
                        </IndexTable.Cell>
                    );
                })}
        </IndexTable.Row>
    ));

    const sortOptions = DataTableConfig.columns
        .filter((col) => col.isSortable)
        .flatMap(function (column) {
            if (column.type === 'date') {
                return [
                    { label: column.title, directionLabel: `Oldest First`, value: `${column.key} asc` },
                    { label: column.title, directionLabel: `Newest First`, value: `${column.key} desc` },
                ];
            } else if (column.type === 'text') {
                return [
                    { label: column.title, directionLabel: `A-Z`, value: `${column.key} asc` },
                    { label: column.title, directionLabel: `Z-A`, value: `${column.key} desc` },
                ];
            } else {
                return [
                    { label: column.title, directionLabel: `Ascending`, value: `${column.key} asc` },
                    { label: column.title, directionLabel: `Descending`, value: `${column.key} desc` },
                ];
            }
        });

    return (
        <>
            <IndexFilters
                sortOptions={sortOptions}
                sortSelected={sortSelected}
                queryValue={searchTerm}
                queryPlaceholder="Search Inventory Adjustments"
                onQueryChange={(term) => setSearchTerm(term)}
                onQueryClear={() => setSearchTerm('')}
                onClearAll={handleFiltersClearAll}
                mode="FILTERING"
                onSort={(selected) => setSortSelected(selected)}
                filters={filterOptions}
                appliedFilters={filters}
                tabs={[]}
                autoFocusSearchField={false}
            />
            <IndexTable
                condensed={useBreakpoints().smDown}
                resourceName={DataTableConfig.resourceName}
                selectable={false}
                itemCount={dataState?.total || 0}
                headings={DataTableConfig.columns.filter((col) => col.isViewable).map((column) => ({ title: column.title }))}
                pagination={{
                    hasPrevious: page > 1,
                    hasNext: page < dataState.pageCount,
                    onNext: () => setPage((p) => p + 1),
                    onPrevious: () => setPage((p) => p - 1),
                    label: dataState.total > 0 ? `${(page - 1) * dataState.pageSize + 1}-${Math.min(page * dataState.pageSize, dataState.total)} of ${dataState.total}` : '',
                }}
            >
                {rowMarkup}
            </IndexTable>
        </>
    );
}
